import React, { useContext, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  InputAdornment,
  ListItemButton,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import { faltaJustificativa, podePedirJustificativa } from '../../utils/exigeJustificativa';

const getSelectionLabel = (statuses, statusId, substatusId) => {
  if (statusId == null || statusId === '') return '';
  const status = (statuses || []).find((s) => Number(s.id) === Number(statusId));
  if (!status) return '';
  if (substatusId != null && substatusId !== '') {
    const sub = (status.substatuses || []).find((s) => Number(s.id) === Number(substatusId));
    if (sub) return `${status.name} · ${sub.name}`;
  }
  return status.name;
};

const StatusJustificationPicker = ({
  statuses = [],
  statusId,
  substatusId,
  onSelect,
  disabled = false,
  error = false,
  helperText = '',
  placeholder = 'Selecione o status',
}) => {
  const { theme } = useContext(ThemeContext);
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const displayValue = useMemo(
    () => getSelectionLabel(statuses, statusId, substatusId),
    [statuses, statusId, substatusId]
  );

  const filteredStatuses = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return statuses;
    return statuses
      .map((status) => {
        const statusName = (status.name || '').toLowerCase();
        const stageName = (status.stage?.name || '').toLowerCase();
        const subs = (status.substatuses || []).filter((sub) =>
          (sub.name || '').toLowerCase().includes(q)
        );
        const statusMatches = statusName.includes(q) || stageName.includes(q);
        if (statusMatches) return status;
        if (subs.length) return { ...status, substatuses: subs };
        return null;
      })
      .filter(Boolean);
  }, [statuses, search]);

  const isStatusSelected = (id) =>
    Number(statusId) === Number(id) && (substatusId == null || substatusId === '');

  const isSubSelected = (sId, subId) =>
    Number(statusId) === Number(sId) && Number(substatusId) === Number(subId);

  const handlePickStatus = (status) => {
    onSelect?.({ statusId: status.id, substatusId: null });
    setSearch('');

    /*
     * Status que EXIGE justificativa não fecha a lista.
     *
     * Fechando, o campo passa a mostrar só o nome do status -- com cara de
     * preenchido -- e a exigência some de vista até virar erro no "Criar".
     * Foi exatamente assim que o Daniel ficou sem conseguir abrir um chamado
     * interno em 28/10/2026: o sistema pedia um campo que, para ele, não
     * existia na tela.
     *
     * Limpar a busca antes é parte do mesmo movimento: quem chegou no status
     * digitando o nome dele veria a lista filtrada, sem as justificativas que
     * precisa escolher agora.
     */
    if (podePedirJustificativa(statuses, status.id, null)) return;

    setOpen(false);
  };

  const handlePickSub = (status, sub) => {
    onSelect?.({ statusId: status.id, substatusId: sub.id });
    setOpen(false);
    setSearch('');
  };

  return (
    <>
      <TextField
        ref={anchorRef}
        fullWidth
        size="small"
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        onClick={() => {
          if (!disabled) setOpen(true);
        }}
        // O campo fechado mostra "Status · Justificativa" e é estreito: o
        // texto inteiro vai no `title`, porque um input corta sem reticências
        // -- não há nem o aviso de que falta texto.
        inputProps={{ readOnly: true, title: displayValue || undefined }}
        helperText={helperText || undefined}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.default,
            cursor: disabled ? 'default' : 'pointer',
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <ExpandMore
                fontSize="small"
                sx={{
                  color: theme.palette.text.secondary,
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                }}
              />
            </InputAdornment>
          ),
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => {
          setOpen(false);
          setSearch('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              /*
               * A lista acompanha a largura do campo, mas nunca fica menor
               * que 340 (Gisele, 19/10/2026: "aumentar o espaço para
               * visualizar melhor").
               *
               * O campo do status é estreito, e a lista herdava essa largura:
               * com o chip da etapa do lado ("Em atendimento", "Aguardando"),
               * sobrava tão pouco para o nome que ele virava "Pende..." e
               * "Em..." -- dois status diferentes com o mesmo rótulo visível,
               * que é pior do que não mostrar nada.
               */
              width: Math.max(anchorRef.current?.offsetWidth || 0, 340),
              maxWidth: '96vw',
              maxHeight: 360,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[4],
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ p: 1.25, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Buscar status ou justificativa..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </Box>

        {/*
          O pedido fica DENTRO da lista, junto das opções que o atendem.
          Embaixo do campo fechado ele seria lido depois de a lista já ter
          coberto a tela.
        */}
        {faltaJustificativa(statuses, statusId, substatusId) && (
          <Box
            role="status"
            sx={{
              px: 1.75,
              py: 1,
              flexShrink: 0,
              borderBottom: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.mode === 'dark'
                ? 'rgba(99, 172, 229, 0.12)'
                : 'rgba(30, 77, 114, 0.08)',
            }}
          >
            <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
              Escolha a justificativa deste status
            </Typography>
          </Box>
        )}

        <Box sx={{ overflowY: 'auto', flex: 1, py: 0.5 }}>
          {filteredStatuses.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
              Nenhum resultado
            </Typography>
          ) : (
            filteredStatuses.map((status, idx) => {
              const subs = Array.isArray(status.substatuses) ? status.substatuses : [];
              const statusSelected = isStatusSelected(status.id);
              return (
                <Box
                  key={status.id}
                  sx={{
                    px: 0.75,
                    pt: idx > 0 ? 0.75 : 0.25,
                    pb: 0.5,
                  }}
                >
                  <ListItemButton
                    selected={statusSelected}
                    onClick={() => handlePickStatus(status)}
                    sx={{
                      borderRadius: 1.5,
                      py: 0.75,
                      px: 1.25,
                      border: '1px solid',
                      borderColor: statusSelected ? theme.palette.primary.main : 'transparent',
                      '&.Mui-selected': {
                        bgcolor: theme.palette.mode === 'dark'
                          ? 'rgba(99, 172, 229, 0.12)'
                          : 'rgba(30, 77, 114, 0.08)',
                      },
                      '&:hover': {
                        bgcolor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                      {/*
                        * O nome do status QUEBRA em vez de ser cortado. Antes
                        * era `noWrap` com o chip ao lado sem encolher nunca:
                        * o nome cedia todo o espaço e sobrava "Pende...".
                        *
                        * Agora, se faltar largura, os dois cedem -- o nome
                        * ganha uma segunda linha e o chip tem teto próprio.
                        * O nome é o que identifica a opção; o chip repete a
                        * etapa, que é informação de apoio.
                        */}
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, flex: 1, minWidth: 0, lineHeight: 1.25, overflowWrap: 'anywhere' }}
                      >
                        {status.name}
                      </Typography>
                      {status.stage?.name ? (
                        <Chip
                          label={status.stage.name}
                          size="small"
                          variant="outlined"
                          title={status.stage.name}
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            flexShrink: 1,
                            maxWidth: '45%',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                          }}
                        />
                      ) : null}
                    </Box>
                  </ListItemButton>

                  {subs.length > 0 ? (
                    <Box
                      sx={{
                        ml: 1.5,
                        mt: 0.5,
                        pl: 1.25,
                        borderLeft: `2px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          px: 0.75,
                          py: 0.35,
                          color: theme.palette.text.secondary,
                          fontWeight: 500,
                          letterSpacing: 0.2,
                        }}
                      >
                        Justificativas
                      </Typography>
                      {subs.map((sub) => {
                        const subSelected = isSubSelected(status.id, sub.id);
                        return (
                          <ListItemButton
                            key={sub.id}
                            dense
                            selected={subSelected}
                            onClick={() => handlePickSub(status, sub)}
                            sx={{
                              borderRadius: 1.25,
                              py: 0.5,
                              px: 1,
                              minHeight: 32,
                              '&.Mui-selected': {
                                bgcolor: theme.palette.mode === 'dark'
                                  ? 'rgba(99, 172, 229, 0.1)'
                                  : 'rgba(30, 77, 114, 0.06)',
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.8125rem',
                                color: theme.palette.text.primary,
                                fontWeight: subSelected ? 500 : 400,
                              }}
                            >
                              {sub.name}
                            </Typography>
                          </ListItemButton>
                        );
                      })}
                    </Box>
                  ) : null}
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
};

export default StatusJustificationPicker;
